import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// BeforeInstallPromptEvent is not in standard TypeScript types
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface InstallState {
  // State
  canPrompt: boolean;
  isInstalled: boolean;
  hasDismissed: boolean;
  isIOS: boolean;
  
  // Non-persisted (event reference)
  deferredPrompt: BeforeInstallPromptEvent | null;

  // Actions
  setDeferredPrompt: (event: BeforeInstallPromptEvent | null) => void;
  setIsInstalled: (value: boolean) => void;
  setIsIOS: (value: boolean) => void;
  promptInstall: () => Promise<boolean>;
  dismiss: () => void;
  reset: () => void;
}

export const useInstallStore = create<InstallState>()(
  persist(
    (set, get) => ({
      // Initial state
      canPrompt: false,
      isInstalled: false,
      hasDismissed: false,
      isIOS: false,
      deferredPrompt: null,

      // Set the deferred prompt event (captured from beforeinstallprompt)
      setDeferredPrompt: (event) => {
        set({ 
          deferredPrompt: event,
          canPrompt: event !== null,
        });
      },

      // Set installed state
      setIsInstalled: (value) => {
        set({ isInstalled: value });
      },

      // Set iOS detection
      setIsIOS: (value) => {
        set({ isIOS: value });
      },

      // Trigger the native install prompt
      promptInstall: async () => {
        const { deferredPrompt } = get();
        
        if (!deferredPrompt) {
          return false;
        }

        try {
          // Show the install prompt
          await deferredPrompt.prompt();
          
          // Wait for user choice
          const { outcome } = await deferredPrompt.userChoice;
          
          // Clear the deferred prompt (can only be used once)
          set({ 
            deferredPrompt: null,
            canPrompt: false,
          });
          
          if (outcome === 'accepted') {
            set({ isInstalled: true });
            return true;
          }
          
          return false;
        } catch (error) {
          console.error('Error prompting install:', error);
          return false;
        }
      },

      // User dismissed the prompt
      dismiss: () => {
        set({ hasDismissed: true });
      },

      // Reset dismissed state (for testing or new sessions)
      reset: () => {
        set({ hasDismissed: false });
      },
    }),
    {
      name: 'refind-install',
      // Only persist hasDismissed - the rest is runtime state
      partialize: (state) => ({ hasDismissed: state.hasDismissed }),
    }
  )
);

